// useDefaultPromo -- the published default composition for a shop (or the
// global banner) without its own promotional swiper.
//
// status: "idle" (disabled -- the page has its own pub, nothing is fetched),
// "loading", "ready" (data.slides to render) or "none" (no published
// configuration / request failed -> the historical banner).
// The result is cached by apiDefaultPromo (one request per target / language
// / loading nonce) and is stable across renders: the composition is resolved
// once, server-side, by the deterministic engine.
import { useEffect, useState, useMemo } from "react";
import { getElakayLanguageCode } from "../pageInside/dataElakay";
import { getPublicDefaultPromo, peekPublicDefaultPromo } from "./apiDefaultPromo";

const toState = (value) => (value && value.slides?.length ? { status: "ready", data: value } : { status: "none", data: value || null });

const useDefaultPromo = ({ target = "shop", salt = "", enabled = true } = {}) => {
  const loadSalt = useMemo(() => {
    if (target !== "shop") return String(salt);
    const nonce = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    return `${String(salt).slice(0, 70)}|${nonce}`;
  }, [salt, target]);
  const lang = getElakayLanguageCode();
  const params = { target, lang, salt: loadSalt.slice(0, 120) };
  const [state, setState] = useState(() => {
    if (!enabled) return { status: "idle", data: null };
    const cached = peekPublicDefaultPromo(params);
    return cached ? toState(cached) : { status: "loading", data: null };
  });

  useEffect(() => {
    if (!enabled) {
      setState({ status: "idle", data: null });
      return undefined;
    }
    let cancelled = false;
    const cached = peekPublicDefaultPromo({ target, lang, salt: params.salt });
    if (cached) setState(toState(cached));
    else setState((prev) => (prev.status === "loading" ? prev : { status: "loading", data: null }));
    const select = async () => {
      let value = await getPublicDefaultPromo({ target, lang, salt: params.salt });
      if (cancelled) return;
      if (target === "shop" && value?.slides?.length && value.renewOnLoad !== false) {
        const key = `elakay-promo-selection:${target}:${String(salt).slice(0, 70)}`;
        const signature = (data) => {
          const serialized = JSON.stringify(data.slides);
          let hash = 2166136261;
          for (let i = 0; i < serialized.length; i += 1) hash = Math.imul(hash ^ serialized.charCodeAt(i), 16777619);
          return String(hash >>> 0);
        };
        let previous;
        try { previous = sessionStorage.getItem(key); } catch { /* Optional history. */ }
        for (let attempt = 1; attempt <= 2 && signature(value) === previous; attempt += 1) {
          const candidate = await getPublicDefaultPromo({ target, lang, salt: `${params.salt.slice(0, 110)}|retry${attempt}` });
          if (cancelled) return;
          if (candidate?.slides?.length) value = candidate;
        }
        try { sessionStorage.setItem(key, signature(value)); } catch { /* Optional history. */ }
      }
      if (!cancelled) setState(toState(value));
    };
    select();
    return () => { cancelled = true; };
  }, [enabled, target, lang, params.salt, salt]);

  return state;
};

export default useDefaultPromo;
